import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/api';

/**
 * Currency context — one place that:
 *   - fetches the hourly-refreshed FX rates from the backend (/currency/rates),
 *   - remembers the user's selected DISPLAY currency (localStorage),
 *   - exposes convert() / format() helpers.
 *
 * Rates are "units of <code> per 1 USD" (base USD). Stored backend amounts are
 * denominated in INR unless a value carries its own currency, so convert()
 * defaults its source currency to INR.
 */

// Supported display currencies (mirrors the backend SUPPORTED_CURRENCIES).
export const CURRENCY_META = {
  USD: { symbol: '$',   label: 'US Dollar',        locale: 'en-US' },
  INR: { symbol: '₹',   label: 'Indian Rupee',     locale: 'en-IN' },
  EUR: { symbol: '€',   label: 'Euro',             locale: 'de-DE' },
  GBP: { symbol: '£',   label: 'British Pound',    locale: 'en-GB' },
  AUD: { symbol: 'AUD', label: 'Australian Dollar', locale: 'en-AU' },
  SGD: { symbol: 'SGD', label: 'Singapore Dollar', locale: 'en-SG' },
  AED: { symbol: 'AED', label: 'UAE Dirham',       locale: 'en-AE' },
};

export const CURRENCY_CODES = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'SGD', 'AED'];

// Currencies rendered with their English ISO code (e.g. "AUD 1,234") instead of
// a symbol — their symbols are ambiguous ($) or non-Latin (د.إ).
export const CODE_NOTATION_CURRENCIES = new Set(['AUD', 'SGD', 'AED']);

const DEFAULT_CURRENCY = 'INR';
// Per-user cache key so one browser can host multiple logins without their
// currency choices bleeding into each other. Guests share a single key.
const cacheKey = (userId) => (userId ? `displayCurrency:${userId}` : 'displayCurrency:guest');

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children, user }) {
  const userId = user?.id || null;
  const [currency, setCurrencyState] = useState(
    () => localStorage.getItem(cacheKey(user?.id)) || DEFAULT_CURRENCY
  );
  const [rates, setRates] = useState({}); // { USD: 1, INR: 96.01, ... }
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load the signed-in user's saved currency (server is source of truth so it
  // follows them across devices), falling back to their per-user local cache.
  // Resets when the logged-in user changes — no cross-login interference.
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      const guest = localStorage.getItem(cacheKey(null));
      setCurrencyState(CURRENCY_META[guest] ? guest : DEFAULT_CURRENCY);
      return;
    }
    // Fast path: this user's cached choice (avoids a flash before the fetch).
    const cached = localStorage.getItem(cacheKey(userId));
    if (cached && CURRENCY_META[cached]) setCurrencyState(cached);
    else if (user?.display_currency && CURRENCY_META[user.display_currency]) setCurrencyState(user.display_currency);
    else setCurrencyState(DEFAULT_CURRENCY);
    // Authoritative: fetch the stored preference.
    api.get('/auth/me')
      .then((res) => {
        if (cancelled) return;
        const c = res.data?.display_currency;
        if (c && CURRENCY_META[c]) {
          setCurrencyState(c);
          localStorage.setItem(cacheKey(userId), c);
        }
      })
      .catch(() => { /* keep cached/default */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Load rates on mount. The backend serves an in-memory cache that it refreshes
  // hourly; right after a backend (re)start that cache can be momentarily empty,
  // so if we get an empty payload we retry a few times until it warms up. Until
  // real rates arrive, format() renders values in their source currency (see
  // displayCurrencyFor) rather than showing an unconverted number under the
  // selected currency's symbol.
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer = null;
    const load = () => {
      api.get('/currency/rates')
        .then((res) => {
          if (cancelled) return;
          const r = res.data?.rates || {};
          if (Object.keys(r).length > 0) {
            setRates(r);
            setLastUpdated(res.data?.last_updated || null);
            return; // got them
          }
          throw new Error('empty rates'); // fall through to retry
        })
        .catch(() => {
          if (cancelled || attempts >= 6) return;
          attempts += 1;
          timer = setTimeout(load, 3000); // backend cache may still be warming up
        });
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const setCurrency = useCallback((code) => {
    const next = CURRENCY_META[code] ? code : DEFAULT_CURRENCY;
    setCurrencyState(next);
    localStorage.setItem(cacheKey(userId), next);
    // Persist to the account so it's retained on next login / other devices.
    if (userId) {
      api.patch('/auth/me', { display_currency: next }).catch(() => { /* cached locally regardless */ });
    }
  }, [userId]);

  // Convert an amount from a source currency (default INR) into the display currency.
  const convert = useCallback((amount, from = 'INR') => {
    const value = Number(amount);
    if (!isFinite(value)) return amount;
    const src = (from || 'INR').toUpperCase();
    const dst = currency;
    if (src === dst) return value;
    const rFrom = rates[src];
    const rDst = rates[dst];
    if (!rFrom || !rDst) return value; // rates unavailable → show raw
    return (value / rFrom) * rDst;
  }, [rates, currency]);

  // The currency we can ACTUALLY render `from` in: the selected one when we have
  // both rates, otherwise the source currency itself. This prevents showing an
  // unconverted number under the selected currency's symbol (e.g. "$96L") when
  // rates haven't loaded — we show "₹96L" until conversion is truly possible.
  const displayCurrencyFor = useCallback((from = 'INR') => {
    const src = (from || 'INR').toUpperCase();
    if (src === currency) return currency;
    return (rates[src] && rates[currency]) ? currency : src;
  }, [rates, currency]);

  // Convert + format with the resolved currency's symbol/locale.
  const format = useCallback((amount, from = 'INR', opts = {}) => {
    const cur = displayCurrencyFor(from);
    const value = cur === currency ? Number(convert(amount, from)) : Number(amount);
    const meta = CURRENCY_META[cur] || CURRENCY_META[DEFAULT_CURRENCY];
    const useCode = CODE_NOTATION_CURRENCIES.has(cur);
    const prefix = useCode ? `${cur} ` : meta.symbol;
    const {
      maximumFractionDigits = Number.isInteger(value) ? 0 : 2,
      minimumFractionDigits = 0,
    } = opts;
    if (!isFinite(value)) return `${prefix}0`;
    try {
      return new Intl.NumberFormat(meta.locale, {
        style: 'currency',
        currency: cur,
        currencyDisplay: useCode ? 'code' : 'symbol',
        maximumFractionDigits,
        minimumFractionDigits,
      }).format(value);
    } catch {
      return `${prefix}${value.toLocaleString()}`;
    }
  }, [convert, currency, displayCurrencyFor]);

  // Convert + format as a COMPACT string for big numbers (dashboards/charts).
  // INR keeps lakh/crore notation; other currencies use K/M/B. Prefixed with the
  // RESOLVED currency's symbol (falls back to source currency until rates load).
  const formatCompact = useCallback((amount, from = 'INR') => {
    const cur = displayCurrencyFor(from);
    const v = cur === currency ? Number(convert(amount, from)) : Number(amount);
    const sym = CODE_NOTATION_CURRENCIES.has(cur)
      ? `${cur} `
      : (CURRENCY_META[cur] || CURRENCY_META[DEFAULT_CURRENCY]).symbol;
    if (!isFinite(v) || v === 0) return `${sym}0`;
    const abs = Math.abs(v);
    if (cur === 'INR') {
      if (abs >= 1e7) return `${sym}${(v / 1e7).toFixed(1)}Cr`;
      if (abs >= 1e5) return `${sym}${(v / 1e5).toFixed(1)}L`;
      if (abs >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}K`;
      return `${sym}${v.toFixed(0)}`;
    }
    if (abs >= 1e9) return `${sym}${(v / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}K`;
    return `${sym}${v.toFixed(0)}`;
  }, [convert, currency, displayCurrencyFor]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    symbol: (CURRENCY_META[currency] || {}).symbol || '',
    rates,
    ratesReady: Object.keys(rates).length > 0,
    lastUpdated,
    codes: CURRENCY_CODES,
    meta: CURRENCY_META,
    convert,
    format,
    formatCompact,
  }), [currency, setCurrency, rates, lastUpdated, convert, format, formatCompact]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback if used outside the provider (e.g. public pages) — renders
    // raw INR-style values without conversion.
    return {
      currency: DEFAULT_CURRENCY,
      setCurrency: () => {},
      symbol: '₹',
      rates: {},
      ratesReady: false,
      lastUpdated: null,
      codes: CURRENCY_CODES,
      meta: CURRENCY_META,
      convert: (a) => a,
      format: (a) => `₹${Number(a || 0).toLocaleString('en-IN')}`,
      formatCompact: (a) => `₹${Number(a || 0).toLocaleString('en-IN')}`,
    };
  }
  return ctx;
}
